import { useUpdateMutation } from '@gripp/shared-logic';
import {
  Button,
  ControlledTextInput,
  CreateQrInvitation,
  colors as CropCircleColors,
  KeyboardSafeView,
  Spacing,
  useAutoFocusRef,
  useSystemNotification
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { object, string } from 'yup';

interface Invitation {
  url: string;
  invitationId: string;
}

type AccountInviteFormProps = {
  onInvitationChange?: (invitation: Invitation) => void;
};

export const AccountInviteForm = ({
  onInvitationChange,
}: AccountInviteFormProps) => {
  const [invitation, setInvitation] = useState<Invitation | undefined>(
    undefined
  );

  const handleSetInvitation = (next: Invitation) => {
    setInvitation(next);
    onInvitationChange?.(next);
  };

  return (
    <View style={styles.container}>
      <View style={styles.createInvitationContainer}>
        <CreateQrInvitation qrSize={350} onSetInvitation={handleSetInvitation} />
      </View>
      <InviteForm invitation={invitation as Invitation} />
    </View>
  )
};

const inviteFormSchema = object({
  emails: string()
    .required()
    .matches(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))(\s*,\s*(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))*$/,
      ''
    ),
});

const InviteForm = ({
  invitation,
}: {
  invitation: Invitation;
}) => {
  const { t } = useTranslation();
  const elementFocusCallBack = useAutoFocusRef();
  const { mutateAsync, isPending } = useUpdateMutation({
    modelName: 'invitation',
    query: UPDATE_INVITATION,
  });
  const { showSuccess, showError } = useSystemNotification();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(inviteFormSchema),
    mode: 'onChange',
    defaultValues: {
      emails: '',
    },
  });

  const onSubmit = async (data: { emails: string }) => {
    const emailList = data.emails.replace(/\s/g, '').split(',');
    const input = {
      recipients: emailList.map((email) => ({ email })),
      id: invitation.invitationId,
    };

    await mutateAsync(
      { input },
      {
        onSuccess: () => {
          reset();
          showSuccess(t('invitation.invitePeople.inviteSentSuccess'));
        },
        onError: () => {
          showError(t('invitation.invitePeople.inviteSentError'));
        }
      }
    );
  };

  return (
    <View>
      <KeyboardSafeView>
        <ScrollView contentContainerStyle={styles.formContainer}>
          <View>
            <View style={styles.formItem}>
              <Text style={styles.formItemTitle}>{t('invitation.invitePeople.inviteViaEmailLabel')}</Text>
              <ControlledTextInput
                control={control}
                name="emails"
                placeholder={t('invitation.invitePeople.emailPlaceholder')}
                innerRef={elementFocusCallBack}
                backgroundColor={CropCircleColors.neutral[200]}
                hideBorder
                useCropCircle
              />
            </View>
          </View>
          <View>
            <Button
              style={styles.saveButton}
              onPress={handleSubmit(onSubmit)}
              state={(!isValid || !isDirty || isPending) ? 'default' : 'active'}
            >
              {t('invitation.invitePeople.send')}
            </Button>
          </View>
        </ScrollView>
      </KeyboardSafeView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  createInvitationContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    height: '100%',
    justifyContent: 'space-between',
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 10,
  },
  formItem: {
    marginBottom: Spacing.baseMargin.marginVertical,
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});

const UPDATE_INVITATION = `
  mutation updateInvitation($input: MutateInvitationInput!) {
    updateInvitation(input: $input) {
      id,
      recipients {
        email
      }
    }
  }
`;